import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { CompanyMark } from "../../Chat/CompanyMark";
import { formatNumber } from "../Charts/utils/formatNumber";
import { WatchlistEarningsEntry } from "../../../hooks/useWatchlistEarnings";

// The reporting date arrives as a midnight-UTC instant standing for a calendar
// date. Parsing it locally renders it a day early anywhere west of UTC — and
// the grouping already reads it as UTC, so a local parse here would print a
// date that contradicts the heading above it.
dayjs.extend(utc);

interface EarningsRowProps {
  entry: WatchlistEarningsEntry;
  onClick: (ticker: string) => void;
  /** Hairline rule above the row; omitted on the first so a group opens flush. */
  showDivider?: boolean;
  /**
   * Driven by the group rather than recomputed here, so the colour can never
   * disagree with the heading it sits under.
   */
  highlight?: boolean;
}

// "in 6 days" carries more at a glance than a date does, but the date is what
// you need to actually plan around — so both, with the relative one leading.
//
// A failed lookup must never render as "Not scheduled": stating that a company
// has nothing due when we simply couldn't find out is the one wrong answer
// this screen can't afford to give.
const relativeLabel = (
  days: number | null,
  unavailable: boolean,
): string => {
  if (unavailable) return "Unavailable";
  if (days === null) return "Not scheduled";
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
};

// Only shown when it isn't USD: labelling every row "USD" is noise, but an
// unlabelled JPY revenue estimate beside a USD one reads as directly
// comparable when it's orders of magnitude out. The code is appended rather
// than rendered as a symbol — FMP reports LSE listings as GBP when the figures
// are pence, so a "£" would assert a precision we don't have.
const estimateSuffix = (currency: string | null): string => {
  if (!currency || currency.toUpperCase() === "USD") return "";
  return ` ${currency.toUpperCase()}`;
};

const estimateLabelSx = (color: string) => ({
  fontSize: "0.6875rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  color,
});

/**
 * One company and when it reports.
 *
 * The reporting block sits immediately beside the company name from `lg` up,
 * not pinned to the right edge: on a wide display that put ~1500px between a
 * ticker and its date, so reading one company meant tracking across the entire
 * screen. Below `lg` nothing moves — at phone width the row is already narrow
 * enough that the right edge is close, and that layout is the one that works.
 */
const EarningsRow: React.FC<EarningsRowProps> = ({
  entry,
  onClick,
  showDivider = true,
  highlight = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const hairline = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)";

  const hasDate = entry.nextEarningsDate !== null;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onClick(entry.ticker)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(entry.ticker);
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1.5, lg: 3 },
        minHeight: 56,
        // Bleeds the hover tint past the list padding so the row reads as part
        // of the list rather than a card sitting inside it.
        mx: -1,
        px: 1,
        cursor: "pointer",
        borderRadius: 1.5,
        borderTop: showDivider ? `1px solid ${hairline}` : "none",
        WebkitTapHighlightColor: "transparent",
        transition: "transform 0.1s ease, background-color 0.18s ease",
        "&:hover": {
          backgroundColor: isDark
            ? "rgba(255, 255, 255, 0.03)"
            : "rgba(0, 0, 0, 0.02)",
        },
        "&:active": { transform: "scale(0.98)" },
        "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: -2 },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:active": { transform: "none" },
        },
      }}
    >
      <CompanyMark symbol={entry.ticker} size={32} radius="5px" />

      {/* Elastic on phones so the date is pushed to the right edge; fixed from
          lg so the date lands in a consistent column right beside it. */}
      <Box sx={{ flex: { xs: 1, lg: "none" }, width: { lg: 260 }, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            letterSpacing: "0.01em",
            color: theme.palette.text.primary,
          }}
        >
          {entry.ticker}
        </Typography>
        <Typography
          noWrap
          sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary }}
        >
          {entry.companyName}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: "none",
          width: { lg: 130 },
          textAlign: { xs: "right", lg: "left" },
        }}
      >
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: highlight ? "#22c55e" : theme.palette.text.primary,
          }}
        >
          {relativeLabel(entry.daysUntilEarnings, entry.earningsUnavailable)}
        </Typography>
        {hasDate && (
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontVariantNumeric: "tabular-nums",
              color: theme.palette.text.secondary,
            }}
          >
            {dayjs.utc(entry.nextEarningsDate).format("D MMM YYYY")}
          </Typography>
        )}
      </Box>

      {/* Absorbs the leftover width so the estimates hold the right edge
          without the reporting block drifting away from the company. */}
      <Box sx={{ display: { xs: "none", lg: "block" }, flex: 1 }} />

      {/* Analyst expectations for the upcoming report — already in the payload
          and, on this screen, the natural next question after "when". Coverage
          varies by company, so a missing figure shows as an em dash rather
          than collapsing the column. */}
      <Box
        sx={{
          display: { xs: "none", lg: "flex" },
          flex: "none",
          gap: 4,
          textAlign: "right",
        }}
      >
        <Box sx={{ width: 96 }}>
          <Typography sx={estimateLabelSx(theme.palette.text.disabled)}>
            EPS est.
          </Typography>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontVariantNumeric: "tabular-nums",
              color: theme.palette.text.primary,
            }}
          >
            {entry.epsEstimated === null
              ? "—"
              : `${entry.epsEstimated.toFixed(2)}${estimateSuffix(entry.currency)}`}
          </Typography>
        </Box>

        <Box sx={{ width: 110 }}>
          <Typography sx={estimateLabelSx(theme.palette.text.disabled)}>
            Rev est.
          </Typography>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontVariantNumeric: "tabular-nums",
              color: theme.palette.text.primary,
            }}
          >
            {entry.revenueEstimated === null
              ? "—"
              : `${formatNumber(entry.revenueEstimated)}${estimateSuffix(entry.currency)}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default EarningsRow;
